import type { LucideIcon } from 'lucide-react';
import { Film, Image, MessageSquare } from 'lucide-react';
import type { RequestBillingModality } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';

/**
 * Outline lucide glyphs for `/requests` (chat / image / video). No chip —
 * the glyph sits bare in a fixed `h-3.5 w-3.5` slot so model names stay
 * left-aligned even when the modality is unknown.
 */

const MODALITY_SLOT_CLASS = 'inline-flex h-3.5 w-3.5 shrink-0';

const MODALITY_ICON: Record<RequestBillingModality, LucideIcon> = {
  chat: MessageSquare,
  image: Image,
  video: Film,
};

export function RequestModalityIcon({
  modality,
  rowId,
}: {
  readonly modality: RequestBillingModality | undefined;
  readonly rowId: string;
}): JSX.Element {
  const t = useT();
  if (modality === undefined) {
    return (
      <span
        aria-hidden="true"
        className={MODALITY_SLOT_CLASS}
        data-testid={`requests-modality-slot-${rowId}`}
      />
    );
  }
  const label = t(`requests.modality.${modality}`);
  const Glyph = MODALITY_ICON[modality];
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={MODALITY_SLOT_CLASS}
      data-testid={`requests-modality-${rowId}`}
    >
      <Glyph aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-low" />
    </span>
  );
}
